import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, TrendingUp, Building, Package, ArrowLeft, CheckCircle, Menu, X } from 'lucide-react';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import ScrollingLogo from '@/components/ScrollingLogo';

export default function ServicesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('meetings');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = {
    meetings: {
      icon: Users,
      title: 'Meetings & Corporate Events',
      description: 'Transform your business meetings into memorable experiences',
      usps: [
        'Access to 500+ premium venues across India',
        'Dedicated event coordinator',
        'Audio-visual equipment & technology support',
        'Catering from 5-star chefs'
      ],
      caseStudy: {
        title: 'Tech Corp Annual Summit',
        description: 'Successfully hosted 300+ attendees at ITC Grand Chola, Chennai with seamless execution',
        result: '98% attendee satisfaction'
      }
    },
    incentives: {
      icon: TrendingUp,
      title: 'Incentive Travel',
      description: 'Reward your top performers with unforgettable experiences',
      usps: [
        'Curated luxury destinations',
        'Team building activities',
        'Exclusive resort bookings',
        'All-inclusive packages'
      ],
      caseStudy: {
        title: 'Sales Team Reward Trip',
        description: 'Organized 5-day incentive trip to Goa for 50 top performers',
        result: '45% increase in team motivation'
      }
    },
    conferences: {
      icon: Building,
      title: 'Conferences & Conventions',
      description: 'Host large-scale conferences with professional support',
      usps: [
        'Convention centers with capacity 1000+',
        'Registration & badge management',
        'Exhibition booth setup',
        'Live streaming & recording'
      ],
      caseStudy: {
        title: 'National Tech Conference',
        description: 'Managed 2000+ attendees conference at Bangalore International Centre',
        result: '100% on-time execution'
      }
    },
    exhibitions: {
      icon: Package,
      title: 'Exhibitions & Trade Shows',
      description: 'Showcase your products with maximum impact',
      usps: [
        'Prime exhibition spaces',
        'Booth design & construction',
        'Lead generation support',
        'Logistics & shipping coordination'
      ],
      caseStudy: {
        title: 'Auto Expo Delhi',
        description: 'Setup premium exhibition booth for leading automobile brand',
        result: '500+ qualified leads'
      }
    }
  };

  const currentService = services[activeTab];
  const Icon = currentService.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-gold font-medium text-sm uppercase tracking-wide">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              OUR SERVICES
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              Corporate Event Solutions for Every Need
            </h1>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              From intimate board meetings to large-scale conferences, we deliver exceptional event experiences
            </p>
          </div>
        </div>
      </div>

      {/* Services Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-12">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto p-2 bg-white rounded-lg border border-slate-200 shadow-md">
            <TabsTrigger value="meetings" className="flex flex-col items-center gap-2 py-4 text-slate-700 data-[state=active]:bg-navy-600 data-[state=active]:text-white rounded-md" data-testid="tab-meetings">
              <Users className="w-6 h-6" />
              <span className="text-sm font-medium">Meetings</span>
            </TabsTrigger>
            <TabsTrigger value="incentives" className="flex flex-col items-center gap-2 py-4 text-slate-700 data-[state=active]:bg-navy-600 data-[state=active]:text-white rounded-md" data-testid="tab-incentives">
              <TrendingUp className="w-6 h-6" />
              <span className="text-sm font-medium">Incentives</span>
            </TabsTrigger>
            <TabsTrigger value="conferences" className="flex flex-col items-center gap-2 py-4 text-slate-700 data-[state=active]:bg-navy-600 data-[state=active]:text-white rounded-md" data-testid="tab-conferences">
              <Building className="w-6 h-6" />
              <span className="text-sm font-medium">Conferences</span>
            </TabsTrigger>
            <TabsTrigger value="exhibitions" className="flex flex-col items-center gap-2 py-4 text-slate-700 data-[state=active]:bg-navy-600 data-[state=active]:text-white rounded-md" data-testid="tab-exhibitions">
              <Package className="w-6 h-6" />
              <span className="text-sm font-medium">Exhibitions</span>
            </TabsTrigger>
          </TabsList>

          {Object.keys(services).map(key => (
            <TabsContent key={key} value={key} className="space-y-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                <div className="space-y-6 bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gold/10 rounded-lg flex items-center justify-center">
                      {React.createElement(services[key].icon, { className: "w-8 h-8 text-gold" })}
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {services[key].title}
                    </h2>
                  </div>
                  <p className="text-lg text-slate-800 leading-relaxed">
                    {services[key].description}
                  </p>
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-slate-900">What We Offer:</h3>
                    <ul className="space-y-3">
                      {services[key].usps.map((usp, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{usp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-wrap gap-4 pt-4">
                    <Button 
                      onClick={() => navigate('/properties')}
                      className="bg-navy-600 hover:bg-navy-700 text-white font-semibold px-6 py-3"
                      data-testid={`plan-${key}-button`}
                    >
                      Plan My Event
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-2 border-gold text-gold hover:bg-gold hover:text-white font-semibold px-6 py-3"
                      data-testid={`consult-${key}-button`}
                      onClick={() => window.open(`https://wa.me/919664870766?text=${encodeURIComponent(`Hi! I'm interested in ${services[key].title} services. Please help me plan my event.`)}`, '_blank')}
                    >
                      Talk to Consultant
                    </Button>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-8 rounded-xl border border-slate-200 shadow-sm">
                  <span className="inline-block bg-gold/10 text-gold text-sm font-bold uppercase px-3 py-1 rounded-full">Case Study</span>
                  <h3 className="text-2xl font-bold mt-4 mb-3 text-slate-900">
                    {services[key].caseStudy.title}
                  </h3>
                  <p className="text-slate-800 mb-6 leading-relaxed">
                    {services[key].caseStudy.description}
                  </p>
                  <div className="bg-white p-5 rounded-lg border border-gold/30 shadow-sm">
                    <p className="text-sm font-bold text-gold uppercase tracking-wide">Result</p>
                    <p className="text-2xl font-bold text-slate-900 mt-2">
                      {services[key].caseStudy.result}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>

        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-navy-700 to-navy-900 text-white p-8 sm:p-12 rounded-xl shadow-xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Start Planning?
          </h2>
          <p className="text-white/90 mb-8 text-base sm:text-lg max-w-2xl mx-auto">
            Let our experts help you create the perfect event experience
          </p>
          <Button 
            onClick={() => navigate('/properties')}
            className="bg-gold hover:bg-gold-light text-navy-900 px-8 py-4 text-base font-bold"
            data-testid="get-started-cta"
          >
            Get Started Today
          </Button>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
